import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function OathPage() {
  const [oaths, setOaths] = useState({
    oath1: false,
    oath2: false,
    oath3: false
  });
  const [formData, setFormData] = useState({ email: '', password: '', name: '', gender: '', referral_code: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [referralValidation, setReferralValidation] = useState(null); // { valid, referrer_name }
  const navigate = useNavigate();

  // فرض الوضع النهاري + قراءة كود الدعوة من URL
  useEffect(() => {
    document.documentElement.removeAttribute('data-theme');
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    if (refCode) {
      const upperCode = refCode.trim().toUpperCase();
      setFormData(prev => ({ ...prev, referral_code: upperCode }));
      // تحقق تلقائي
      axios.post(`${BACKEND_URL}/api/referral/validate`, { referral_code: upperCode })
        .then(r => setReferralValidation(r.data))
        .catch(() => setReferralValidation({ valid: false }));
    }
  }, []);

  // التحقق من تحديد جميع خانات القَسَم والجنس
  const allOathsChecked = oaths.oath1 && oaths.oath2 && oaths.oath3;
  const canSubmit = allOathsChecked && formData.gender;

  // تحقق يدوي من كود الدعوة عند الكتابة (بعد التوقف عن الكتابة)
  const validateReferralCode = async (code) => {
    if (!code || code.length < 4) {
      setReferralValidation(null);
      return;
    }
    try {
      const r = await axios.post(`${BACKEND_URL}/api/referral/validate`, { referral_code: code });
      setReferralValidation(r.data);
    } catch {
      setReferralValidation({ valid: false });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!allOathsChecked) {
      setError('يجب أن توافق على جميع بنود القَسَم أولاً');
      return;
    }
    if (!formData.gender) {
      setError('يرجى تحديد الجنس');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/oath`, formData, {
        withCredentials: true
      });

      if (response.data.user_id) {
        // حفظ بيانات المستخدم للصفحة الترحيبية
        localStorage.setItem('userName', formData.name);
        localStorage.setItem('userGender', formData.gender);
        localStorage.setItem('userId', response.data.user_id);
        navigate('/welcome');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'حدث خطأ');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container islamic-pattern" style={{ 
      display: 'flex', 
      justifyContent: 'center', 
      alignItems: 'center',
      minHeight: '100vh',
      padding: '20px',
      textAlign: 'center'
    }}>
      <div className="card" style={{ 
        maxWidth: '480px', 
        width: '100%', 
        padding: '28px 20px', 
        margin: '0 auto',
        textAlign: 'center'
      }}>
        
        {/* القسم العلوي - الصور والشعار مع تأثير أوراق الورد */}
        <div style={{ textAlign: 'center', marginBottom: '20px' }}>
          <div className="rose-fall-container" style={{ display: 'inline-block', position: 'relative', marginBottom: '8px' }}>
            {/* فراشات بيضاء صغيرة تطير خلف وفوق العروسين */}
            <div className="butterfly butterfly-1" />
            <div className="butterfly butterfly-2" />
            <div className="butterfly butterfly-3" />
            <div className="butterfly butterfly-4" />
            <div className="butterfly butterfly-5" />
            <div className="butterfly butterfly-6" />

            {/* صورة العروسين - مكبرة */}
            <img 
              src="https://customer-assets.emergentagent.com/job_islamic-match-7/artifacts/52pcu015_file_000000001f4471fda83fb27db773137e.png" 
              alt="وِداد - تطبيق الخطابة"
              style={{ 
                width: '280px', 
                height: 'auto',
                display: 'block',
                margin: '0 auto',
                borderRadius: '16px'
              }}
            />
          </div>
          
          {/* شعار وداد - الصورة الجديدة */}
          <img 
            src="https://customer-assets.emergentagent.com/job_nikah-finder-12/artifacts/f962jdfh_IMG_%D9%A2%D9%A0%D9%A2%D9%A6%D9%A0%D9%A1%D9%A0%D9%A2_%D9%A0%D9%A2%D9%A0%D9%A2%D9%A4%D9%A3.png" 
            alt="وِداد - WeDaD"
            style={{ 
              width: '220px', 
              height: 'auto',
              display: 'block',
              margin: '-10px auto 0'
            }}
          />
          
          {/* الشعار النصي */}
          <p style={{ 
            fontSize: '20px', 
            color: '#00897B', 
            marginTop: '5px',
            marginBottom: '0',
            fontWeight: '600'
          }}>
            حيث تلتقي القلوب المتشابهة 💕
          </p>
        </div>
        
        {/* التعريف بالموقع */}
        <div style={{ 
          background: 'linear-gradient(135deg, rgba(0, 180, 162, 0.08), rgba(32, 178, 170, 0.08))', 
          padding: '18px', 
          borderRadius: '12px', 
          marginBottom: '24px',
          border: '1px solid rgba(0, 180, 162, 0.15)'
        }}>
          <p style={{ 
            fontSize: '16px', 
            color: 'var(--text-dark)', 
            lineHeight: '2',
            fontFamily: "'Noto Naskh Arabic', serif",
            textAlign: 'center',
            margin: 0
          }}>
            أسميناه <strong style={{ color: '#00897B' }}>وِداد</strong> ❣️<br/>
            لأن الود يكبر حين يجمع بين قلبين تجمعهما الاهتمامات والوظيفة 💞<br/>
            حيث تتقارب الأفكار، ويتشابه اليوم 🌼<br/>
            فتبدأ الحكاية بفهمٍ صادق<br/>
            وتستمر بودٍّ يدوم طويلاً 🫂
          </p>
        </div>

        <div className="divider" style={{ margin: '20px 0' }}></div>

        {/* قسم القَسَم */}
        <div style={{ marginBottom: '24px', padding: '20px', background: 'var(--light-bg)', borderRadius: '12px' }}>
          {/* العنوان الجديد */}
          <div style={{ 
            textAlign: 'center', 
            marginBottom: '20px',
            padding: '20px',
            background: 'linear-gradient(135deg, rgba(0, 137, 123, 0.08), rgba(0, 180, 162, 0.05))',
            borderRadius: '16px',
            border: '2px solid rgba(0, 137, 123, 0.15)'
          }}>
            <p style={{ 
              fontSize: '13px', 
              color: '#00897B', 
              marginBottom: '8px',
              letterSpacing: '2px',
              fontWeight: '500'
            }}>
              ✦ مرحباً بك ✦
            </p>
            <h2 style={{ 
              fontSize: '22px', 
              color: '#00695C', 
              marginBottom: '0',
              fontFamily: "'Tajawal', sans-serif", 
              fontWeight: '700',
              lineHeight: '1.6'
            }}>
              عزيزي الزائر<br/>
              <span style={{ 
                color: '#00897B',
                fontSize: '22px',
                fontFamily: "'Tajawal', sans-serif",
                fontWeight: '700'
              }}>
                تسجيلك يستلزم القسم بالله
              </span>
            </h2>
          </div>
          
          <div style={{ background: 'white', padding: '16px', borderRadius: '8px', marginBottom: '16px' }}>
            <p style={{ 
              lineHeight: '1.8', 
              fontSize: '16px', 
              color: '#D32F2F', 
              fontFamily: "'Amiri', serif",
              textAlign: 'center',
              marginBottom: '12px',
              fontWeight: '700'
            }}>
              أقسم بالله العظيم أنني:
            </p>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              <div className="checkbox-container" style={{ 
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(0,137,123,0.05))', 
                padding: '10px 14px', 
                borderRadius: '10px',
                border: '1px solid rgba(0,137,123,0.1)',
                transition: 'all 0.2s ease'
              }}>
                <input 
                  type="checkbox" 
                  className="checkbox"
                  checked={oaths.oath1}
                  onChange={(e) => setOaths({...oaths, oath1: e.target.checked})}
                  id="oath1"
                />
                <label htmlFor="oath1" className="checkbox-label" style={{ fontWeight: '500' }}>
                  جاد في طلب الزواج
                </label>
              </div>
              
              <div className="checkbox-container" style={{ 
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(0,137,123,0.05))', 
                padding: '10px 14px', 
                borderRadius: '10px',
                border: '1px solid rgba(0,137,123,0.1)',
                transition: 'all 0.2s ease'
              }}>
                <input 
                  type="checkbox" 
                  className="checkbox"
                  checked={oaths.oath2}
                  onChange={(e) => setOaths({...oaths, oath2: e.target.checked})}
                  id="oath2"
                />
                <label htmlFor="oath2" className="checkbox-label" style={{ fontWeight: '500' }}>
                  صادق في معلوماتي
                </label>
              </div>
              
              <div className="checkbox-container" style={{ 
                background: 'linear-gradient(135deg, rgba(255,255,255,0.9), rgba(0,137,123,0.05))', 
                padding: '10px 14px', 
                borderRadius: '10px',
                border: '1px solid rgba(0,137,123,0.1)',
                transition: 'all 0.2s ease'
              }}>
                <input 
                  type="checkbox" 
                  className="checkbox"
                  checked={oaths.oath3}
                  onChange={(e) => setOaths({...oaths, oath3: e.target.checked})}
                  id="oath3"
                />
                <label htmlFor="oath3" className="checkbox-label" style={{ fontWeight: '500' }}>
                  سأحترم الآخرين
                </label>
              </div>
            </div>
          </div>

          <p style={{ fontSize: '14px', color: 'var(--error)', marginTop: '8px', textAlign: 'center', lineHeight: '1.6', fontFamily: "'Tajawal', sans-serif", fontWeight: '600' }}>
            تذكر : القسم بالله أمر عظيم !<br/>
            فاحذر من الحنث به
          </p>
        </div>

        {error && <div className="message message-error">{error}</div>}

        <form onSubmit={handleSubmit}>
          {/* اختيار الجنس - مهم جداً */}
          <div className="form-group" style={{ marginBottom: '24px' }}>
            <label className="form-label" style={{ 
              textAlign: 'center', 
              display: 'block', 
              marginBottom: '16px',
              fontSize: '17px',
              fontWeight: '600',
              color: '#00897B'
            }}>
              أنا...
            </label>
            <div style={{ 
              display: 'flex', 
              gap: '16px', 
              justifyContent: 'center'
            }}>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, gender: 'male' })}
                style={{
                  flex: 1,
                  maxWidth: '180px',
                  padding: '20px 24px',
                  borderRadius: '16px',
                  border: formData.gender === 'male' 
                    ? '3px solid #4A90D9' 
                    : '2px solid rgba(0,0,0,0.1)',
                  background: formData.gender === 'male' 
                    ? 'linear-gradient(135deg, rgba(74, 144, 217, 0.15), rgba(74, 144, 217, 0.05))' 
                    : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: formData.gender === 'male' ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: formData.gender === 'male' 
                    ? '0 8px 25px rgba(74, 144, 217, 0.3)' 
                    : '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>👨</div>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: '700',
                  color: formData.gender === 'male' ? '#4A90D9' : '#333'
                }}>
                  رجل
                </div>
              </button>

              <button
                type="button"
                onClick={() => setFormData({ ...formData, gender: 'female' })}
                style={{
                  flex: 1,
                  maxWidth: '180px',
                  padding: '20px 24px',
                  borderRadius: '16px',
                  border: formData.gender === 'female' 
                    ? '3px solid #E91E8C' 
                    : '2px solid rgba(0,0,0,0.1)',
                  background: formData.gender === 'female' 
                    ? 'linear-gradient(135deg, rgba(233, 30, 140, 0.15), rgba(233, 30, 140, 0.05))' 
                    : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  transform: formData.gender === 'female' ? 'scale(1.02)' : 'scale(1)',
                  boxShadow: formData.gender === 'female' 
                    ? '0 8px 25px rgba(233, 30, 140, 0.3)' 
                    : '0 2px 8px rgba(0,0,0,0.05)'
                }}
              >
                <div style={{ fontSize: '40px', marginBottom: '8px' }}>👩</div>
                <div style={{ 
                  fontSize: '18px', 
                  fontWeight: '700',
                  color: formData.gender === 'female' ? '#E91E8C' : '#333'
                }}>
                  امرأة
                </div>
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">الاسم الكامل</label>
            <input
              type="text"
              className="input"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              placeholder="أدخل اسمك الكامل"
            />
          </div>

          <div className="form-group">
            <label className="form-label">البريد الإلكتروني</label>
            <input
              type="email"
              className="input"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              placeholder="example@email.com"
            />
          </div>

          <div className="form-group password-field">
            <label className="form-label">كلمة المرور</label>
            <div className="password-input-wrapper">
              <input
                type={showPassword ? 'text' : 'password'}
                className="input password-input"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                required
                minLength="6"
                placeholder="6 أحرف على الأقل"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? 'إخفاء كلمة المرور' : 'إظهار كلمة المرور'}
              >
                {showPassword ? <Eye size={20} /> : <EyeOff size={20} />}
              </button>
            </div>
          </div>

          {/* كود الدعوة - اختياري */}
          <div className="form-group">
            <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '6px' }}>
              <span>🎁 كود خصم أو كود دعوة <span style={{ color: 'var(--text-light)', fontWeight: '400' }}>(اختياري)</span></span>
              <span style={{ fontSize: '11px', color: 'var(--text-light)', fontWeight: '400' }}>
                للحصول على 3 أيام Premium مجاناً
              </span>
            </label>
            <input
              type="text"
              data-testid="referral-code-input"
              className="input"
              value={formData.referral_code}
              onChange={(e) => {
                const code = e.target.value.toUpperCase();
                setFormData({ ...formData, referral_code: code });
                validateReferralCode(code);
              }}
              placeholder="إذا لم يكن لديك، اتركه فارغاً"
              style={{ direction: 'rtl', textAlign: 'right', fontFamily: 'inherit', letterSpacing: '0px' }}
              maxLength="20"
            />
            <p style={{ fontSize: '11px', color: 'var(--text-light)', marginTop: '6px', textAlign: 'right', lineHeight: 1.7 }}>
              💡 احصل على كود دعوة من صديق مسجّل في وِداد، أو من حسابك بعد التسجيل عبر صفحة "ادعُ صديقاً"
            </p>
            {referralValidation && formData.referral_code && (
              <div style={{ 
                marginTop: '6px',
                fontSize: '12px',
                color: referralValidation.valid ? 'var(--success)' : 'var(--error)',
                fontWeight: '600',
                textAlign: 'right'
              }}>
                {referralValidation.valid 
                  ? `✅ كود صحيح! دعوة من ${referralValidation.referrer_name} — ستحصل على 3 أيام Premium`
                  : '❌ كود غير صحيح'}
              </div>
            )}
          </div>

          <button type="submit" className="btn btn-primary btn-full" disabled={loading || !canSubmit} data-testid="register-btn" style={{ background: canSubmit ? 'linear-gradient(135deg, #00897B, #00B4A2)' : '#ccc', fontSize: '16px' }}>
            {loading ? '⏳ جاري التسجيل...' : '✨ ابدأ رحلتك في وِداد'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button 
            onClick={() => navigate('/login')}
            style={{ background: 'none', border: 'none', color: '#00897B', cursor: 'pointer', fontSize: '14px' }}
          >
            لديك حساب؟ سجل الدخول
          </button>
        </div>

        <div className="divider" style={{ margin: '20px 0' }}></div>

        <div style={{ background: 'rgba(0, 137, 123, 0.08)', padding: '16px', borderRadius: '10px', textAlign: 'center' }}>
          <h3 style={{ fontSize: '16px', marginBottom: '10px', color: '#00897B', fontFamily: "'Noto Naskh Arabic', serif" }}>💕 رؤيتنا</h3>
          <p style={{ lineHeight: '1.8', color: 'var(--text-dark)', fontSize: '13px', fontFamily: "'Noto Naskh Arabic', serif", margin: 0 }}>
            وِداد يجمع بين الأشخاص الذين يشتركون في الاهتمامات والمجال المهني والتخصص،
            لأن التوافق الحقيقي يبدأ من القيم والاهتمامات المشتركة.
          </p>
        </div>

        <div style={{ textAlign: 'center', marginTop: '16px', fontSize: '12px', color: 'var(--text-light)' }}>
          بالتسجيل، أنت توافق على شروط الاستخدام وسياسة الخصوصية الموضّحة بالأسفل
        </div>

        {/* Footer Links - Required for Payment Gateway Approval */}
        <div style={{ 
          textAlign: 'center', 
          marginTop: '24px', 
          paddingTop: '16px',
          borderTop: '1px solid var(--border-color)',
          fontSize: '12px', 
          color: 'var(--text-light)' 
        }}>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '14px', flexWrap: 'wrap', marginBottom: '8px' }}>
            <span 
              data-testid="oath-footer-terms"
              onClick={() => navigate('/terms')} 
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              الشروط والأحكام
            </span>
            <span 
              data-testid="oath-footer-privacy"
              onClick={() => navigate('/privacy')} 
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              سياسة الخصوصية
            </span>
            <span 
              data-testid="oath-footer-refund"
              onClick={() => navigate('/refund-policy')} 
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              سياسة الاسترداد
            </span>
            <span 
              data-testid="oath-footer-contact"
              onClick={() => navigate('/contact')} 
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              اتصل بنا
            </span>
          </div>
          <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '11px' }}>
            © 2026 Widad (وِداد) — Membership-Based Matrimonial Community
          </p>
        </div>
      </div>
    </div>
  );
}

export default OathPage;
